import {
  CreatePortfolioRequest,
  PortfolioResult,
  PortfolioService,
} from '@/api';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { zodResolver } from '@hookform/resolvers/zod';
import { useStore } from '@nanostores/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { $portfolios } from '@/stores/portfolioStore';

const formSchema = z.object({
  portfolioName: z.string().min(2, {
    message: 'Portfolio name must be at least 2 characters.',
  }),
  portfolioType: z.enum(['voluntary_churned', 'involuntary_churned', 'accounts_receivable']).optional(),
  portfolioDesc: z.string().optional(),
});

interface Props {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  organizationId: string;
}

export function NewPortfolioModal({ open, setOpen, organizationId }: Props) {
  // query client
  const queryClient = useQueryClient();
  const portfolios = useStore($portfolios);

  // Define the form.
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      portfolioName: '',
      portfolioType: 'accounts_receivable',
      portfolioDesc: '',
    },
  });

  // mutation to create a new portfolio
  const createPortfolio = useMutation({
    mutationFn: async (payload: CreatePortfolioRequest) => {
      // call the api to create a new portfolio
      const res = await PortfolioService.createPortfolio(payload);
      return res;
    },
    onSuccess: data => {
      // invalidate queries here so that they are refetched
      queryClient.invalidateQueries({
        queryKey: ['getPortfolios', organizationId],
      });

      const newPortfolio: PortfolioResult = {
        portfolio: data,
        invites: 0,
        collaborators: [],
        accountReceivablesCount: 0,
      };

      $portfolios.set([newPortfolio, ...portfolios]);

      // close the modal and reset the form
      setOpen(false);
      form.reset();
    },
    onError: error => {
      console.error(error);

      // close the modal and reset the form
      setOpen(false);
      form.reset();

      // show a toast message
      toast('An error occurred', {
        description: error.message,
        duration: 5000,
      });
    },
  });

  // Define a submit handler.
  async function onSubmit(values: z.infer<typeof formSchema>) {
    // call the mutation
    createPortfolio.mutateAsync({
      name: values.portfolioName,
      description: values.portfolioDesc,
      orgId: organizationId,
    });
  }

  return (
    <Dialog open={open} onOpenChange={o => {
      form.reset();
      setOpen(o);
    }}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Create New Account Group</DialogTitle>
          <DialogDescription>
            Create a group to organize related accounts. You can add multiple accounts to each group.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="mt-4 space-y-8"
          >
            <FormField
              control={form.control}
              name="portfolioName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Account Group Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Client 1" {...field} />
                  </FormControl>
                  <FormDescription>
                    Enter the name for the Account group.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="portfolioType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Account Type</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select an account type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="accounts_receivable">Accounts Receivable</SelectItem>
                      <SelectItem value="voluntary_churned">Voluntary Churned</SelectItem>
                      <SelectItem value="involuntary_churned">Involuntary Churned</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Select the type of accounts this group will contain.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="portfolioDesc"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Account Group Description</FormLabel>
                  <FormControl>
                    <Input placeholder="" {...field} />
                  </FormControl>
                  <FormDescription>
                    Provide a short description of the account group.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end space-x-2">
              <Button variant="outline" type="button" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createPortfolio.isPending}>
                {createPortfolio.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Create
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
} 